import React from "react";
import { SnackbarContext } from "../../../../../../contexts/Snackbar";
import { GlobalDataContext } from "../../../../../../contexts/GlobalData";
import { useTranslation } from "react-i18next";
import { OPEN_CONVO } from "../../../../../../enums/ApiPaths";
import { Controller, FormProvider, useForm } from "react-hook-form";
import usePut from "../../../../../../hooks/usePut";
import ActionRow from "../../../../../admin/clinic/components/actions/ActionRow";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import { Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, FormControlLabel, Grid, IconButton, Paper, Stack, Typography } from "@mui/material";
import { VIEW_TEMPLATES } from "../../../../../../enums/PermissionTitles";
import TemplateAutoComplete from "../../../../../appointments/form/TemplateAutoComplete";
import { TemplateCategory } from "../../../../../../enums/TemplateCategory";
import UserHint from "../../../../../../components/display/UserHint";
import CharCountField from "../../../../../../components/form/CharCountField";
import { useIsMobile } from "../../../../../../utils/Functions";

// Regex matcher to find HTML tags
var htmlRegexG = /<(?:"[^"]*"['"]*|'[^']*'['"]*|[^'">])+>/g;

export default function NotifyOwner({ pet }) {
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const { t } = useTranslation();
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const isMobile = useIsMobile();
  async function handleSetValue(value) {
    const val = !value
      ? value
      : String(value?.description).replace(htmlRegexG, "");
    methods.setValue("message", val);
  }

  const [openDialog, setOpenDialog] = React.useState(false);
  const methods = useForm({
    defaultValues: {
      message: "",
      notifyViaEmail: false,
    },
  });
  const apiPath = OPEN_CONVO;
  const put = usePut(apiPath, ["conversations"], "conversations");

  const handleDialog = () => {
    setOpenDialog((prev) => !prev);
    if (!openDialog) {
      methods.reset();
    }
  };

  async function onSubmit(data) {
    if (!data?.message?.trim()) {
      handleSnackbar("MessageMissing", "error");
      return;
    }
    if (!pet.userId) {
      handleSnackbar("OwnerMissing", "error");
      return;
    }

    const dto = {
      message: data.message.trim(),
      id: pet.userId,
      notifyViaEmail: data.notifyViaEmail,
    };

    const res = await put.submitCallback(dto);

    if (res) {
      handleDialog();
      handleSnackbar("MessageSent", "success");
    }
  }

  return (
    <>
      <ActionRow
        icon={<EmailIcon />}
        label={t("NotifyOwner")}
        description={t("SendMessageOrEmail")}
        onClick={handleDialog}
      />

      <Dialog
        open={openDialog}
        onClose={handleDialog}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? "" : 3,
            overflow: isMobile ? "" : "hidden",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 2,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <EmailIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h6" component="h2" fontWeight={700}>
              {t("NotifyOwner")}
            </Typography>
          </Stack>
          <IconButton onClick={handleDialog} size="small" color="error" title={t("Close")}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <DialogContent sx={{ px: 3, py: 3 }}>
              <Stack spacing={3}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: "action.hover",
                    borderRadius: 2,
                  }}
                >
                  <Stack spacing={1.5}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      {t("RecipientInformation")}
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <InfoRow label={t("OwnerName")} value={pet.ownerName} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InfoRow
                          label={t("OwnerEmail")}
                          value={pet.ownerEmail}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <InfoRow label={t("PetName")} value={pet.name} />
                      </Grid>
                    </Grid>
                  </Stack>
                </Paper>

                <Stack spacing={2.5}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={2}
                  >
                    {isPermissionAllowed(VIEW_TEMPLATES) && (
                      <Box sx={{ flex: 1, width: { xs: "100%", sm: "auto" } }}>
                        <TemplateAutoComplete
                          control={methods.control}
                          handleSetValue={handleSetValue}
                          category={TemplateCategory.NOTIFY_OWNER}
                          placeholder={t("SelectTemplate")}
                        />
                      </Box>
                    )}
                    <Controller
                      name="notifyViaEmail"
                      control={methods.control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                              color="primary"
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {t("NotifyViaEmail")}
                            </Typography>
                          }
                          sx={{ mr: 0 }}
                        />
                      )}
                    />
                    <UserHint hint={"NofifyViaEmailUserHint"} iconOnly />
                  </Stack>

                  <Controller
                    name="message"
                    control={methods.control}
                    rules={{
                      required: true,
                      validate: (value) => value?.trim().length > 0,
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <CharCountField
                        {...field}
                        label={t("Message")}
                        multiline
                        rows={6}
                        inputProps={{ maxLength: 5000 }}
                        fullWidth
                        variant="outlined"
                        error={!!error}
                        helperText={
                          error
                            ? t("MessageRequired")
                            : t("MessageCharacterLimit", { count: 5000 })
                        }
                        placeholder={t("WriteYourMessageHere")}
                      />
                    )}
                  />
                </Stack>
              </Stack>
            </DialogContent>

            <DialogActions
              sx={{ px: 3, py: 2, borderTop: 1, borderColor: "divider" }}
            >
              <Button
                variant="contained"
                type="submit"
                disabled={!methods.watch("message")?.trim() || put.isLoading}
                sx={{
                  px: 4,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {put.isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t("Send")
                )}
              </Button>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
    </>
  );
}


function InfoRow({ label, value }) {
  return (
    <Stack direction="row" spacing={1} alignItems="baseline">
      <Typography variant="body2" color="text.secondary" fontWeight={500}>
        {label}:
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value || "-"}
      </Typography>
    </Stack>
  );
}

